/* global document */
import { copySelectedToClipboard } from 'app/utils/env';
import React, { Fragment, FunctionComponent, useState, useEffect } from 'react';
import BaseButton from 'app/components/common/BaseButton';
import { useClasses } from 'app/utils/css/index';
import { getLinkStyles } from 'app/components/presentation/link/styles';
import { ButtonA } from 'app/components/presentation/buttons/ButtonClassNameA';
import { ExtractProps } from 'app/utils/types';

const COPY_TIMEOUT_MS = 1500;

export const useCopyToClipboard = (value: string) => {
  const [isCopied, setCopied] = useState(false);

  useEffect(() => {
    if (isCopied) {
      const timeout = setTimeout(() => {
        setCopied(false);
      }, COPY_TIMEOUT_MS);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [isCopied]);

  const classes = useClasses({
    textarea: {
      height: '0',
      left: '-100vw',
      position: 'fixed',
      width: '0',
    },
  });

  const copy = () => {
    const textarea = document.createElement('textarea');
    textarea.className = classes.textarea;
    textarea.value = value;

    try {
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      copySelectedToClipboard();
      setCopied(true);
    } finally {
      document.body.removeChild(textarea);
    }
  };

  return { isCopied, copy };
};

interface IProps {
  value: string;
  label: string;
  copiedMessage: string;
}

export const CopyToClipboardLink: FunctionComponent<IProps> = ({
  value,
  label,
  copiedMessage,
}) => {
  const classes = useClasses({
    link: getLinkStyles(),
  });

  const { copy, isCopied } = useCopyToClipboard(value);

  if (isCopied) {
    return <Fragment>
      {copiedMessage}
    </Fragment>;
  } else {
    return <Fragment>
      <BaseButton className={classes.link} onClick={copy}>
        {label}
      </BaseButton>
    </Fragment>;
  }
};

type ButtonProps = IProps & ExtractProps<typeof ButtonA>;

export const CopyToClipboardButton: FunctionComponent<ButtonProps> = ({
  value,
  label,
  copiedMessage,
  ...buttonProps
}) => {
  const { isCopied, copy } = useCopyToClipboard(value);

  return <Fragment>
    <ButtonA {...buttonProps} disabled={isCopied} onClick={copy}>
      {!isCopied
        ? label
        : copiedMessage
      }
    </ButtonA>
  </Fragment>;
};
